import { useEffect, useState } from 'react';
import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useTenantStore } from '@/hooks/useTenantStore';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface Company {
  id: string;
  name: string;
}

export function CompanySelector() {
  const { tenantId, setTenantId } = useTenantStore();
  const [open, setOpen] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      // Get companies user created
      const { data: ownedCompanies, error: ownedError } = await supabase
        .from('companies')
        .select('id, name')
        .eq('created_by', user.id);

      if (ownedError) throw ownedError;

      // Get company IDs user has roles in
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('company_id')
        .eq('user_id', user.id)
        .not('company_id', 'is', null);

      if (roleError) throw roleError;

      // Get the companies for those IDs
      const roleCompanyIds = roleData?.map((r) => r.company_id).filter(Boolean) || [];
      let roleCompanies: Company[] = [];

      if (roleCompanyIds.length > 0) {
        const { data: roleCompaniesFetched, error: roleCompaniesError } = await supabase
          .from('companies')
          .select('id, name')
          .in('id', roleCompanyIds);

        if (roleCompaniesError) throw roleCompaniesError;
        roleCompanies = roleCompaniesFetched || [];
      }

      // Combine and deduplicate
      const companiesMap = new Map<string, Company>();
      ownedCompanies?.forEach((c) => companiesMap.set(c.id, c));
      roleCompanies.forEach((c) => companiesMap.set(c.id, c));

      const allCompanies = Array.from(companiesMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      setCompanies(allCompanies);

      // Auto-select first company if none selected
      if (!tenantId && allCompanies.length > 0) {
        setTenantId(allCompanies[0].id);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const selectedCompany = companies.find((c) => c.id === tenantId);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[250px] justify-between"
        >
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">
              {loading ? 'Loading...' : selectedCompany?.name || 'Select company...'}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0">
        <Command>
          <CommandInput placeholder="Search companies..." />
          <CommandList>
            <CommandEmpty>No companies found.</CommandEmpty>
            <CommandGroup>
              {companies.map((company) => (
                <CommandItem
                  key={company.id}
                  value={company.name}
                  onSelect={() => {
                    setTenantId(company.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      tenantId === company.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {company.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
